import React from 'react';
import config from '../config/environment';
import supabase from '../config/supabase';

type ErrorSource = 'render' | 'window.error' | 'unhandledrejection';

interface ReportGlobalErrorOptions {
  source: ErrorSource;
  error: unknown;
  componentStack?: string | null;
  eventContext?: Record<string, unknown>;
}

const MAX_FIELD_LENGTH = 12_000;
const recentErrorKeys = new Map<string, number>();
const DEDUPE_WINDOW_MS = 5_000;

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function truncate(value: string | null): string | null {
  if (!value) {
    return null;
  }

  return value.length > MAX_FIELD_LENGTH
    ? `${value.slice(0, MAX_FIELD_LENGTH)}...[truncated]`
    : value;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  const normalized = normalizeString(error);
  if (normalized) {
    return normalized;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return 'Erro desconhecido no frontend.';
  }
}

function isAbortError(error: unknown): boolean {
  if (error == null) {
    return false;
  }

  if (typeof error === 'object') {
    const name = 'name' in error && typeof error.name === 'string' ? error.name : '';
    const message = 'message' in error && typeof error.message === 'string'
      ? error.message.toLowerCase()
      : '';

    if (name === 'AbortError' || name === 'NavigatorLockAcquireTimeoutError') {
      return true;
    }

    if (message.includes('signal is aborted') || message.includes('aborted without reason')) {
      return true;
    }
  }

  if (typeof error === 'string') {
    const normalized = error.toLowerCase();
    return normalized.includes('signal is aborted') || normalized.includes('aborted without reason');
  }

  return false;
}

function getErrorStack(error: unknown): string | null {
  return error instanceof Error ? error.stack ?? null : null;
}

function buildDedupeKey(options: ReportGlobalErrorOptions): string {
  return [
    options.source,
    getErrorMessage(options.error),
    getErrorStack(options.error) ?? '',
    options.componentStack ?? '',
  ].join('|');
}

function shouldSkipDuplicate(options: ReportGlobalErrorOptions): boolean {
  const key = buildDedupeKey(options);
  const now = Date.now();
  const lastSeenAt = recentErrorKeys.get(key);

  recentErrorKeys.set(key, now);

  recentErrorKeys.forEach((timestamp, existingKey) => {
    if (now - timestamp > DEDUPE_WINDOW_MS) {
      recentErrorKeys.delete(existingKey);
    }
  });

  return Boolean(lastSeenAt && now - lastSeenAt < DEDUPE_WINDOW_MS);
}

async function getAccessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

export async function reportGlobalError(options: ReportGlobalErrorOptions): Promise<void> {
  if (!config.supabaseFunctionsUrl || isAbortError(options.error) || shouldSkipDuplicate(options)) {
    return;
  }

  const token = await getAccessToken();
  const message = getErrorMessage(options.error);
  const stack = truncate(getErrorStack(options.error));

  try {
    await fetch(`${config.supabaseFunctionsUrl}/frontend-logs`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        operation: options.source,
        message,
        stack,
        context: {
          componentStack: truncate(options.componentStack ?? null),
          location: window.location.href,
          userAgent: window.navigator.userAgent,
          ...options.eventContext,
        },
      }),
    });
  } catch (logError) {
    console.warn('[global-error-logging] Failed to send frontend error log', logError);
  }
}

export function initializeGlobalErrorLogging(): void {
  window.addEventListener('error', (event) => {
    if (isAbortError(event.error ?? event.message)) {
      return;
    }

    void reportGlobalError({
      source: 'window.error',
      error: event.error ?? event.message,
      eventContext: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isAbortError(event.reason)) {
      return;
    }

    void reportGlobalError({
      source: 'unhandledrejection',
      error: event.reason,
    });
  });
}

interface GlobalErrorBoundaryProps {
  children: React.ReactNode;
}

interface GlobalErrorBoundaryState {
  hasError: boolean;
}

export class GlobalErrorBoundary extends React.Component<
  GlobalErrorBoundaryProps,
  GlobalErrorBoundaryState
> {
  state: GlobalErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): GlobalErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    void reportGlobalError({
      source: 'render',
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24 }}>
          <h1>Algo deu errado.</h1>
          <p>O erro foi registrado para análise. Recarregue a página e tente novamente.</p>
        </div>
      );
    }

    return this.props.children;
  }
}
